import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { GitMerge, Loader2, AlertTriangle } from "lucide-react";

type Choice = "primary" | "duplicate";

type Preview = {
  primary: { id: string; full_name: string; preferred_name: string | null; email: string | null; phone: string | null; status: string };
  duplicate: { id: string; full_name: string; preferred_name: string | null; email: string | null; phone: string | null; status: string };
  conflicts: Array<{ field: string; primary_value: any; duplicate_value: any }>;
  counts: Record<string, number>;
  aliases_to_add: (string | null)[];
};

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  primaryId: string | null;
  duplicateId: string | null;
  onMerged?: (mergeId: string) => void;
}

/** Reusable merge UI. Works for instructors today; the entity_type dispatch
 * will extend to parents/students/vendors/partners in later phases. */
export default function MergeDialog({ open, onOpenChange, primaryId, duplicateId, onMerged }: Props) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [reason, setReason] = useState("");
  const [step, setStep] = useState<"preview" | "confirm">("preview");
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !primaryId || !duplicateId) return;
    setStep("preview"); setError(null); setPreview(null); setChoices({}); setReason("");
    setLoading(true);
    (async () => {
      const { data, error } = await (supabase as any).rpc("merge_instructors_preview", {
        _primary: primaryId, _duplicate: duplicateId,
      });
      if (error) { setError(error.message); setLoading(false); return; }
      setPreview(data as Preview);
      const initial: Record<string, Choice> = {};
      (data as Preview).conflicts.forEach((c) => { initial[c.field] = "primary"; });
      setChoices(initial);
      setLoading(false);
    })();
  }, [open, primaryId, duplicateId]);

  const totalMoved = useMemo(() => {
    if (!preview) return 0;
    return Object.values(preview.counts).reduce((a, b) => a + Number(b || 0), 0);
  }, [preview]);

  const execute = async () => {
    if (!preview) return;
    setExecuting(true); setError(null);
    const { data, error } = await (supabase as any).rpc("merge_instructors_execute", {
      _primary: preview.primary.id,
      _duplicate: preview.duplicate.id,
      _field_choices: choices,
      _reason: reason || null,
    });
    setExecuting(false);
    if (error) { setError(error.message); toast.error(error.message); return; }
    toast.success(`Merged ${preview.duplicate.full_name} → ${preview.primary.full_name}`);
    onMerged?.(data as string);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-4 w-4" /> Merge Instructor Records
          </DialogTitle>
          <DialogDescription>
            Nothing is deleted. All history moves to the primary record and the duplicate is redirected.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading merge preview…
          </div>
        ) : error ? (
          <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : preview ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded border border-green-500/40 bg-green-50/40 p-3">
                <div className="text-[10px] uppercase tracking-wide text-green-700 font-semibold">Keep (Primary)</div>
                <div className="font-medium">{preview.primary.full_name}</div>
                <div className="text-xs text-muted-foreground">{preview.primary.email || "—"} · {preview.primary.phone || "—"}</div>
                <Badge variant="outline" className="mt-1">{preview.primary.status}</Badge>
              </div>
              <div className="rounded border border-amber-500/40 bg-amber-50/40 p-3">
                <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Merge (Duplicate)</div>
                <div className="font-medium">{preview.duplicate.full_name}</div>
                <div className="text-xs text-muted-foreground">{preview.duplicate.email || "—"} · {preview.duplicate.phone || "—"}</div>
                <Badge variant="outline" className="mt-1">{preview.duplicate.status}</Badge>
              </div>
            </div>

            <div className="rounded border p-3 text-sm space-y-1 bg-muted/30">
              <div className="font-medium text-xs uppercase text-muted-foreground">This merge will…</div>
              <ul className="text-xs space-y-0.5">
                <li>✓ Preserve <b>{preview.primary.full_name}</b>'s instructor ID</li>
                <li>✓ Move <b>{preview.counts.assignments ?? 0}</b> assignment(s)</li>
                <li>✓ Move <b>{preview.counts.aliases ?? 0}</b> alias(es) and add "{preview.duplicate.full_name}" as an alias</li>
                <li>✓ Move <b>{preview.counts.one_time_payments ?? 0}</b> one-time payment(s)</li>
                <li>✓ Move <b>{preview.counts.onboarding_packets ?? 0}</b> onboarding packet(s) and all compliance</li>
                <li>✓ Move <b>{preview.counts.audit_log ?? 0}</b> audit entries</li>
                <li>✓ Move <b>{preview.counts.import_records ?? 0}</b> import record(s) and <b>{preview.counts.import_changes ?? 0}</b> import change(s)</li>
                <li>✓ Move <b>{preview.counts.waiting_items ?? 0}</b> waiting item(s), <b>{preview.counts.proposals ?? 0}</b> proposal(s), <b>{preview.counts.classes ?? 0}</b> class(es)</li>
                <li>✓ Redirect future references to <b>{preview.primary.full_name}</b></li>
                <li>✓ Fully reversible via Undo</li>
              </ul>
              <div className="text-[10px] text-muted-foreground pt-1">{totalMoved} total related records will move.</div>
            </div>

            {preview.conflicts.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Conflict Resolution — {preview.conflicts.length} field(s)
                </div>
                <div className="rounded border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Field</TableHead>
                        <TableHead>Primary (Keep)</TableHead>
                        <TableHead>Duplicate</TableHead>
                        <TableHead className="w-[140px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.conflicts.map((c) => (
                        <TableRow key={c.field}>
                          <TableCell className="text-xs font-medium">{c.field.replace(/_/g, " ")}</TableCell>
                          <TableCell className="text-xs">{String(c.primary_value ?? "—")}</TableCell>
                          <TableCell className="text-xs">{String(c.duplicate_value ?? "—")}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                type="button" size="sm"
                                variant={choices[c.field] === "primary" ? "default" : "outline"}
                                onClick={() => setChoices((s) => ({ ...s, [c.field]: "primary" }))}
                              >Keep</Button>
                              <Button
                                type="button" size="sm"
                                variant={choices[c.field] === "duplicate" ? "default" : "outline"}
                                onClick={() => setChoices((s) => ({ ...s, [c.field]: "duplicate" }))}
                              >Use</Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Both values are preserved in audit history. "Use" replaces the primary's value with the duplicate's.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="merge-reason" className="text-xs">Reason (optional but recommended)</Label>
              <Input id="merge-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Same person, different nickname" />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={executing}>Cancel</Button>
          {preview && step === "preview" && (
            <Button onClick={() => setStep("confirm")} disabled={loading}>Review & Merge</Button>
          )}
          {preview && step === "confirm" && (
            <Button onClick={execute} disabled={executing}>
              {executing ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" /> Merging…</>) : "Confirm Merge"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
